// Room 6's moderation (ai_app_backend plan task r6-moderation; cards R6-13 to R6-16), through the doorway in the
// `moderation` area (switch.ts, the flag `v2_room6_moderation`): the report button (the picture page's and the
// full-screen slide's), and staff's tools, /mod's report feed, approve and delete, the picture page's moderator delete,
// and an admin's delete of someone else's picture (the picture page's and a card's trash). With the area off, each
// call sends exactly the request its screen sent before (tests/moderation-api.test.mjs); on v2 (moderation-v2.ts):
// `POST /v2/reports`, `GET /v2/staff/reports`, `POST /v2/staff/media-approvals` and `POST /v2/staff/media-removals`,
// answered in the shapes the screens read from v1.
//
// Who sees staff's buttons, and /mod: on v2, `roles` in v2's `GET /v2/me` (`staffRoles`), not the build's lists of
// uids (NEXT_PUBLIC_MODERATOR_UIDS, NEXT_PUBLIC_ADMIN_UID), which stay for a visitor on v1. Either way the page only
// hides buttons: v1 and v2 each check the caller's role themselves.
//
// A removal of one of v2's pictures or clips (`m_…`) goes to v2 whatever the switch says, as the owner's delete does
// (generate/api.ts, `deleteMedia`): v1's routes never delete v2's rows, which carry v2's own bookkeeping.
import { call, lazySide, onlyOnV2, signedInJson } from './doorway.ts'
import type { V2Side } from './doorway.ts'
import { isV2Media } from './media-ids.ts'

const AREA = 'moderation'

type Sides = typeof import('./moderation-v2.ts')

const PATHS = {
  report: '/api/report_image',
  feed: '/api/mod/get_report_feed',
  approve: '/api/mod/approve_image',
  remove: '/api/mod/delete_image',
  // The owner's delete, which v1 lets its admin use on anyone's picture (generate/api.ts, `deleteMedia`).
  ownerDelete: '/api/delete_image',
} as const

/** A call's v2 side from moderation-v2.ts, loaded only when it is used; when it fails to load, v1 at `path`. */
const onV2 = (path: string, use: (loaded: Sides) => V2Side): V2Side =>
  lazySide(() => import('./moderation-v2.ts'), use, path)

const post = (token: string, body: unknown): RequestInit => ({
  method: 'POST',
  headers: signedInJson(token),
  body: JSON.stringify(body),
})

/** The picture or clip as the screen holds it: v1 takes the whole record, v2 only its id. */
export type Reported = { id: string } & Record<string, unknown>

/**
 * Reports someone's public picture or clip with the reasons ticked (the screens' words: 'Underage content', …). v1
 * answers `{ status: 'success' }` (with `approved` when staff approved it); the screens read nothing of it.
 */
export const reportMedia = (
  token: string,
  imageData: Reported,
  reasons: string[],
) =>
  call(AREA, PATHS.report, post(token, { imageData, reasons }), {
    token,
    side: onV2(
      PATHS.report,
      (loaded) => (t, init) => loaded.report(t, init, imageData.id, reasons),
    ),
  })

/** What /mod asks for: v1's page number, or on v2 the cursor the page before answered (`next`). */
export type FeedAsk = {
  dateRange: string
  limit: number
  page: number
  /** v2's cursor for this page: the `next` of the page before; null for the first. v1 ignores it. */
  after: string | null
}

/**
 * A page of the report feed, `{ images }` of v1's picture records, most reported first. On v2 the answer also carries
 * `next`, the cursor of the page after (null on the last), and a page holds at most 100.
 */
export const reportFeed = (token: string, ask: FeedAsk) =>
  call(
    AREA,
    PATHS.feed,
    post(token, {
      date_range: ask.dateRange,
      limit: ask.limit,
      page: ask.page,
    }),
    {
      token,
      side: onV2(
        PATHS.feed,
        (loaded) => (t, init) => loaded.feed(t, init, ask),
      ),
    },
  )

/** Approves a reported picture or clip: it keeps its place and leaves the feed. The screens read only the status. */
export const approveMedia = (token: string, imageId: string) =>
  call(AREA, PATHS.approve, post(token, { image_id: imageId }), {
    token,
    side: onV2(
      PATHS.approve,
      (loaded) => (t, init) => loaded.approve(t, init, imageId),
    ),
  })

/**
 * A moderator's delete, with its reason for the log, sent to v1 as the screen builds it: /mod names the maker
 * `user_id`, the picture page `user_uid`. v2 takes the id and the reason only. A failure answers `{ error }`.
 */
export const removeAsModerator = (
  token: string,
  body: {
    image_id: string
    reason?: string
    user_id?: string
    user_uid?: string
  },
) =>
  call(AREA, PATHS.remove, post(token, body), {
    token,
    ownedByV2: isV2Media(body.image_id),
    side: onV2(
      PATHS.remove,
      (loaded) => (t, init) =>
        loaded.remove(t, init, body.image_id, body.reason ?? null),
    ),
  })

/**
 * An admin's delete of someone else's picture or clip (the picture page's and a card's trash): on v1 the owner's
 * delete, as the screens sent it (`DELETE /api/delete_image`); on v2 staff's removal, without a reason.
 */
export const removeOthers = (
  token: string,
  body: { imageId: string; imageUid: string },
) =>
  call(
    AREA,
    PATHS.ownerDelete,
    {
      method: 'DELETE',
      headers: signedInJson(token),
      body: JSON.stringify(body),
    },
    {
      token,
      ownedByV2: isV2Media(body.imageId),
      side: onV2(
        PATHS.ownerDelete,
        (loaded) => (t, init) => loaded.remove(t, init, body.imageId, null),
      ),
    },
  )

/**
 * The staff roles v2 says this person holds now (`roles` in `GET /v2/me`: 'moderator', 'admin', …) when the switch
 * puts them on v2, an empty list when v2 can't say; null for a visitor on v1, whose buttons follow the build's lists.
 */
export async function staffRoles(token: string): Promise<string[] | null> {
  let response: Response | null
  try {
    response = await onlyOnV2(
      AREA,
      token,
      async () => (await import('./moderation-v2.ts')).roles,
    )
  } catch {
    return [] // on v2, and v2 couldn't say in time: no staff buttons
  }
  if (!response) return null
  if (!response.ok) return []
  const body = await response.json().catch(() => null)
  return Array.isArray(body?.roles)
    ? body.roles.filter((role: unknown) => typeof role === 'string')
    : []
}

/** Roles that may moderate: v2's report feed, approvals and removals take either (as v1's moderator check does). */
export const MODERATING = ['moderator', 'admin']
